// ./src/components/features/VenuesTable/VenuesTable.tsx
import React, { useMemo } from "react";
import { Text } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";

import { Venue } from "@/utils/api/VenueService";

export interface VenuesTableProps {
  /** Array of venues to display */
  venues: Venue[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditVenue?: (venue: Venue) => void;

  /** Callback when remove action is clicked */
  onRemoveVenue?: (venue: Venue) => void;

  /** Callback when view action is clicked */
  onViewVenue?: (venue: Venue) => void;

  /** Callback when go into venue (rooms) is clicked */
  onGoToRooms?: (venue: Venue | null | undefined) => void;

  /** ID of venue currently being removed */
  removingVenueId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const VenuesTable: React.FC<VenuesTableProps> = ({
  venues,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditVenue,
  onRemoveVenue,
  onViewVenue,
  onGoToRooms,
  removingVenueId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<Venue>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Venue Name",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      // {
      //   key: "rooms",
      //   label: "Rooms",
      //   sortable: false,
      //   width: 200,
      //   minWidth: 200,
      //   hideOnMobile: false,
      //   render: (_, record) => {
      //     return (
      //       onGoToRooms &&
      //       record && (
      //         <AppButton
      //           variant="secondary"
      //           onClick={() => onGoToRooms(record)}
      //           label="Rooms"
      //         />
      //       )
      //     );
      //   },
      // },
      {
        key: "city",
        label: "Location",
        sortable: true,
        width: 200,
        minWidth: 200,
        hideOnMobile: true,
        render: (_, record) => {
          const parts = [
            record.city,
            record.stateProvince,
            record.country,
          ].filter(Boolean);
          return parts.length > 0 ? parts.join(", ") : "-";
        },
      },

      {
        key: "contactPerson",
        label: "Contact",
        width: 160,
        minWidth: 160,
        hideOnMobile: true,
        render: (value, record) => {
          if (!value && !record.phone && !record.email) {
            return "-";
          }
          return (
            <div>
              {value && <Text size="sm">{value}</Text>}
              {record.phone && (
                <Text size="xs" c="dimmed">
                  {record.phone}
                </Text>
              )}
              {record.email && (
                <Text size="xs" c="dimmed">
                  {record.email}
                </Text>
              )}
            </div>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 140,
        minWidth: 140,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 150,
        minWidth: 150,
        align: "left",
        render: (_, record) => (
          <RecordActions
            context={record}
            onViewClick={undefined}
            onEditClick={onEditVenue}
            onRemoveClick={onRemoveVenue}
            //onGoInClick={onGoToRooms}
            removeInProgress={removingVenueId === record.id}
          />
        ),
      },
    ],
    [onViewVenue, onEditVenue, onRemoveVenue, onGoToRooms, removingVenueId]
  );

  return (
    <AppTable
      data={venues}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(venue) => venue?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
